import { useMemo, useState } from 'react';
import { BookmarkPlus, Dumbbell, Plus, Search, Trash2, X } from 'lucide-react';
import { Page, EmptyState } from '@/components/Page';
import { inputClass } from '@/components/ui/Field';
import { RestTimer } from '@/features/workout/RestTimer';
import { SetLogger } from '@/features/workout/SetLogger';
import {
  addExerciseToWorkout,
  addSet,
  applyRoutine,
  removeExerciseFromWorkout,
  removeSet,
  saveRoutine,
  useExercises,
  useLastSet,
  useRoutines,
  useWorkout,
} from '@/features/workout/useWorkout';
import { BodyWeightCard } from '@/features/workout/BodyWeightCard';
import { CustomExerciseForm } from '@/features/workout/CustomExerciseForm';
import { bestOneRm, isEffectiveSet } from '@/engine/oneRm';
import type { Exercise, WorkoutSet } from '@/engine/types';
import { longDate, num, todayIso } from '@/lib/format';
import { normalize } from '@/lib/search';
import { cn } from '@/lib/utils';

export default function WorkoutPage() {
  const date = todayIso();
  const workout = useWorkout(date);
  const exercises = useExercises();
  const routines = useRoutines();

  const [picking, setPicking] = useState(false);
  const [savingRoutine, setSavingRoutine] = useState(false);
  const [routineName, setRoutineName] = useState('');

  const byId = useMemo(() => new Map(exercises.map((e) => [e.id, e])), [exercises]);
  const order = workout?.exerciseOrder ?? [];

  return (
    <Page
      title="Entreno"
      subtitle={longDate(date)}
      action={
        order.length > 0 ? (
          <button
            type="button"
            onClick={() => setSavingRoutine(true)}
            aria-label="Guardar como plantilla"
            className="tap shrink-0 rounded-xl border border-[var(--color-border)] px-3 text-[var(--color-text-muted)]"
          >
            <BookmarkPlus aria-hidden="true" className="size-5" />
          </button>
        ) : null
      }
    >
      <div className="flex flex-col gap-5">
        <BodyWeightCard date={date} />

        {order.length === 0 ? (
          <>
            <EmptyState
              icon={<Dumbbell aria-hidden="true" className="size-8" />}
              title="Sesión vacía"
              hint="Añade el primer ejercicio, o carga una de tus plantillas."
            />
            {routines.length > 0 ? (
              <section aria-labelledby="plantillas">
                <h2 id="plantillas" className="mb-2 text-sm font-semibold">
                  Tus plantillas
                </h2>
                <ul className="flex flex-col gap-2">
                  {routines.map((routine) => (
                    <li key={routine.id}>
                      <button
                        type="button"
                        onClick={() => void applyRoutine(date, routine)}
                        className="tap w-full rounded-xl border border-[var(--color-border)] px-3 text-left text-sm font-medium"
                      >
                        {routine.name}
                        <span className="block text-xs font-normal text-[var(--color-text-muted)]">
                          {routine.exerciseIds.length} ejercicios
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : (
          <>
            <RestTimer />
            {order.map((exerciseId) => {
              const exercise = byId.get(exerciseId);
              if (!exercise) return null;
              const sets = (workout?.sets ?? []).filter((s) => s.exerciseId === exerciseId);
              return (
                <ExerciseBlock
                  key={exerciseId}
                  date={date}
                  exercise={exercise}
                  sets={sets}
                  onRemove={() => void removeExerciseFromWorkout(date, exerciseId)}
                />
              );
            })}
          </>
        )}

        <button
          type="button"
          onClick={() => setPicking(true)}
          className="tap flex items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] px-4 font-medium text-[var(--color-text-muted)]"
        >
          <Plus aria-hidden="true" className="size-5" />
          Añadir ejercicio
        </button>
      </div>

      {picking ? (
        <ExercisePicker
          exercises={exercises}
          already={order}
          onPick={(id) => {
            void addExerciseToWorkout(date, id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}

      {savingRoutine ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Guardar plantilla"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
        >
          <div className="safe-bottom w-full max-w-lg rounded-t-3xl border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <h2 className="text-base font-semibold">Guardar como plantilla</h2>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              Se guardan los {order.length} ejercicios en este orden, sin las series.
            </p>
            <input
              type="text"
              aria-label="Nombre de la plantilla"
              placeholder="Empuje A"
              value={routineName}
              onChange={(e) => setRoutineName(e.target.value)}
              className={cn(inputClass, 'mt-3')}
            />
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setSavingRoutine(false)}
                className="tap flex-1 rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={routineName.trim().length < 2}
                onClick={() => {
                  void saveRoutine(routineName, order);
                  setRoutineName('');
                  setSavingRoutine(false);
                }}
                className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-4 text-sm font-semibold text-[var(--color-on-brand)] disabled:opacity-50"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Page>
  );
}

function ExerciseBlock({
  date,
  exercise,
  sets,
  onRemove,
}: {
  date: string;
  exercise: Exercise;
  sets: WorkoutSet[];
  onRemove: () => void;
}) {
  const last = useLastSet(exercise.id, date);
  const oneRm = bestOneRm(sets);
  // Quitar un ejercicio con series se lleva sus series: se pregunta antes.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  return (
    <section
      aria-labelledby={`ej-${exercise.id}`}
      className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--color-border)] p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id={`ej-${exercise.id}`} className="font-semibold">
            {exercise.name}
          </h2>
          <p className="text-xs text-[var(--color-text-muted)]">
            {exercise.repRange.min}–{exercise.repRange.max} reps
            {oneRm != null ? ` · 1RM estimado ${num(oneRm, 1)} kg` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => (sets.length === 0 ? onRemove() : setConfirmingRemove(true))}
          aria-label={`Quitar ${exercise.name}`}
          className="tap rounded-lg px-2 text-[var(--color-text-muted)]"
        >
          <X aria-hidden="true" className="size-5" />
        </button>
      </div>

      {confirmingRemove ? (
        <div
          role="alertdialog"
          aria-label={`Quitar ${exercise.name}`}
          className="flex flex-col gap-2 rounded-xl border border-[var(--color-danger)] p-3 text-sm"
        >
          <p>
            ¿Quitar {exercise.name} y {sets.length === 1 ? 'su serie' : `sus ${sets.length} series`}{' '}
            de hoy?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmingRemove(false)}
              className="tap flex-1 rounded-xl border border-[var(--color-border)] px-3 font-medium"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="tap flex-1 rounded-xl bg-[var(--color-danger)] px-3 font-semibold text-white"
            >
              Quitar
            </button>
          </div>
        </div>
      ) : null}

      {sets.length > 0 ? (
        <ol className="flex flex-col gap-1">
          {sets.map((set, index) => (
            <li
              key={set.id}
              className={cn(
                'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm tabular',
                set.warmup ? 'bg-[var(--color-warn-soft)]' : 'bg-[var(--color-surface-2)]',
              )}
            >
              <span className="w-5 shrink-0 text-[var(--color-text-muted)]">{index + 1}</span>
              <span className="flex-1">
                {num(set.weightKg, set.weightKg % 1 === 0 ? 0 : 1)} kg × {set.reps}
                {set.warmup ? (
                  <span className="ml-1.5 text-xs text-[var(--color-text-muted)]">
                    calentamiento
                  </span>
                ) : (
                  <span className="ml-1.5 text-xs text-[var(--color-text-muted)]">
                    RIR {set.rir}
                    {isEffectiveSet(set) ? '' : ' · no efectiva'}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => void removeSet(date, set.id)}
                aria-label={`Quitar serie ${index + 1} de ${exercise.name}`}
                className="tap rounded-lg px-1 text-[var(--color-text-muted)]"
              >
                <Trash2 aria-hidden="true" className="size-4" />
              </button>
            </li>
          ))}
        </ol>
      ) : null}

      {/* `key`: la última serie de otra sesión llega de Dexie un instante DESPUÉS
          del primer pintado, y el registro ya había cogido sus valores por
          defecto. Al cambiar la serie de referencia se vuelve a montar con ella. */}
      <SetLogger
        key={(sets.at(-1) ?? last)?.id ?? 'sin-historial'}
        exercise={exercise}
        last={sets.at(-1) ?? last}
        onAdd={(set) => void addSet(date, { ...set, exerciseId: exercise.id })}
      />
    </section>
  );
}

function ExercisePicker({
  exercises,
  already,
  onPick,
  onClose,
}: {
  exercises: Exercise[];
  already: string[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const results = useMemo(() => {
    const clean = normalize(query.trim());
    const pool = exercises.filter((e) => !already.includes(e.id));
    if (clean.length < 2) return pool.slice(0, 40);
    return pool.filter((e) => normalize(e.name).includes(clean)).slice(0, 40);
  }, [exercises, already, query]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Elegir ejercicio"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
    >
      <div className="safe-bottom flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-3xl border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Añadir ejercicio</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="tap rounded-lg px-2 text-[var(--color-text-muted)]"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>

        {creating ? (
          <CustomExerciseForm onCreated={onPick} onClose={() => setCreating(false)} />
        ) : (
          <>
            <div className="relative mt-3">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-[var(--color-text-muted)]"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Press, remo, sentadilla…"
                aria-label="Buscar ejercicio"
                autoComplete="off"
                className={cn(inputClass, 'pl-10')}
              />
            </div>

            <ul className="mt-2 flex-1 divide-y divide-[var(--color-border)] overflow-y-auto">
              {results.map((exercise) => (
                <li key={exercise.id}>
                  <button
                    type="button"
                    onClick={() => onPick(exercise.id)}
                    className="tap w-full py-2.5 text-left"
                  >
                    <span className="block text-sm">{exercise.name}</span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      {exercise.equipment.replace('_', ' ')} · {exercise.repRange.min}–
                      {exercise.repRange.max} reps
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* El catálogo no puede tener todas las máquinas de todos los
                gimnasios: lo que falte se crea aquí mismo. */}
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="tap mt-2 flex shrink-0 items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-border)] px-4 text-sm font-medium text-[var(--color-text-muted)]"
            >
              <Plus aria-hidden="true" className="size-4" />
              Crear ejercicio propio
            </button>
          </>
        )}
      </div>
    </div>
  );
}
